import type { BuzzCardState } from '@/features/buzz/api/state'
import { URLS } from '@/pages/urls'
import { Spinner } from '@/ui-kit'
import { formatNumber, formatWholePoints } from '@/utils'
import { Trans } from '@lingui/react/macro'
import clsx from 'clsx'
import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { HIVE_CALLS_CTA_CLASS_NAME } from './BuzzOnboarding'
import { UnlinkBuzzButton } from './UnlinkBuzzButton'

const FIGURE_LABEL_CLASS_NAME = 'text-gray-90 text-[16px]/[24px] font-normal'

/**
 * A figure with no value. The glyph is hidden from assistive tech and a visually-hidden
 * sibling says what it means, rather than an `aria-label`: ARIA prohibits a name on a
 * generic element, so `aria-label` on a bare <span> is exposed inconsistently across
 * screen readers. `Dialog.tsx:62` uses the same sr-only pattern.
 */
const Unavailable = () => (
  <>
    <span aria-hidden="true">—</span>
    <span className="sr-only">
      <Trans>Not available</Trans>
    </span>
  </>
)

/**
 * No figure is wrapped in a `TextTooltip`: the design shows none, and the tooltips the old
 * card carried (a negative Available, an estimated Pending) retired with those figures.
 */
const Figure = ({
  label,
  value,
  className,
  valueClassName
}: {
  label: ReactNode
  value: string | null
  className?: string
  valueClassName: string
}) => (
  <div className={clsx('flex min-w-0 flex-col gap-1', className)}>
    <dt className={FIGURE_LABEL_CLASS_NAME}>{label}</dt>
    <dd className={clsx('m-0 break-words', valueClassName)}>{value === null ? <Unavailable /> : value}</dd>
  </div>
)

/**
 * The linked `/calls` content: the connected header (it replaces the page's "Calls" title),
 * the can't-load message when the Buzz read is unavailable, the three figures, the Hive
 * Calls and unlink controls, and the small print.
 *
 * Each figure renders on its own: a `null` is "—" for that figure only. The can't-load
 * message keys on `isBuzzUnavailable`, never on "some figure is null" — a missing Points
 * earned or Calls completed (the import switched off, a failed ClickHouse read) is not a
 * Buzz outage. A Buzz outage blanks Pending only.
 *
 * The refresh indicator sits in a 24×24 slot that is always rendered, so a background
 * refetch never moves the header. `Spinner` does not forward props, hence the wrapping
 * span carrying the test id.
 *
 * Desktop (from `tablet:`): the header has its own bottom border, and the figures form a
 * 372px block with Points earned and Calls completed on one row. Below `tablet:` the
 * header is followed by a separate divider, and every figure sits on its own row with a
 * divider between each.
 */
export const BuzzConnected = ({ state }: { state: Extract<BuzzCardState, { kind: 'linked' }> }) => (
  <div className="flex w-full flex-col gap-8">
    <div className="tablet:border-b tablet:border-gray-100 tablet:pb-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-h4 text-white-to-black">
          <Trans>Hive Calls is connected!</Trans>
        </h1>
        <span aria-hidden="true" className="flex size-6 shrink-0 items-center justify-center">
          {state.isRefreshing && (
            <span data-testid="buzz-refreshing" className="flex">
              <Spinner size="small" />
            </span>
          )}
        </span>
      </div>
      <p className="text-gray-80 max-w-[800px] text-[16px]/[24px] font-normal">
        <Trans>
          Your Hive Calls account is linked to DataHive AI. Points earned from eligible calls will be credited to this
          account automatically.
        </Trans>
      </p>
    </div>

    <div aria-hidden="true" className="tablet:hidden h-px w-full bg-gray-100" />

    {state.isBuzzUnavailable && (
      <p aria-live="polite" className="text-gray-80 text-[16px]/[24px] font-normal">
        <Trans>Some Hive Calls figures can&apos;t be loaded right now. Try again later.</Trans>
      </p>
    )}

    <div className="tablet:w-[372px] flex w-full flex-col gap-8">
      <dl className="tablet:grid-cols-[auto_minmax(0,1fr)] grid grid-cols-1">
        <Figure
          label={<Trans>Points earned</Trans>}
          value={state.pointsEarned === null ? null : formatWholePoints(state.pointsEarned)}
          className="tablet:relative tablet:border-b tablet:pr-8 tablet:pb-8 tablet:after:absolute tablet:after:top-0 tablet:after:right-0 tablet:after:bottom-8 tablet:after:w-px tablet:after:bg-gray-100 border-b border-gray-100 pb-6"
          valueClassName="text-h2 text-orange font-semibold"
        />
        <Figure
          label={<Trans>Calls completed</Trans>}
          value={state.paidCalls === null ? null : formatNumber(state.paidCalls, 0)}
          className="tablet:pt-0 tablet:pb-8 tablet:pl-8 border-b border-gray-100 py-6"
          valueClassName="text-h2 text-white-to-black font-semibold"
        />
        <Figure
          label={<Trans>Pending points</Trans>}
          value={state.pending === null ? null : formatWholePoints(state.pending)}
          className="tablet:col-span-2 tablet:w-[230px] tablet:pt-8 pt-6"
          valueClassName="text-h5 text-white-to-black"
        />
      </dl>

      <div className="flex items-center gap-2">
        <Link to={URLS.HIVE_CALLS_ANDROID_APP} target="_blank" rel="noreferrer" className={HIVE_CALLS_CTA_CLASS_NAME}>
          <Trans>Open Hive Calls</Trans>
        </Link>
        <UnlinkBuzzButton />
      </div>
    </div>

    <ul
      data-testid="buzz-small-print"
      className="text-body-2 text-gray-90 flex max-w-[800px] list-disc flex-col gap-2 pl-5"
    >
      <li>
        <Trans>
          Points earned counts every Hive Calls point credited to this DataHive account, including from Hive Calls
          accounts you linked before.
        </Trans>
      </li>
      <li>
        <Trans>
          Calls completed counts calls credited with points to this DataHive account, including from Hive Calls accounts
          you linked before, minus calls whose points were later taken back from it.
        </Trans>
      </li>
      <li>
        <Trans>Points earned and Calls completed update together. Pending points updates separately.</Trans>
      </li>
      <li>
        <Trans>Referral points add to Points earned without adding calls.</Trans>
      </li>
      <li>
        <Trans>
          A call that has passed review may not appear in Pending points, Points earned or Calls completed for a while.
        </Trans>
      </li>
      <li>
        <Trans>
          Right after you link, Points earned and Calls completed can show 0 or less than you expect while your Hive
          Calls history is brought over. If that Hive Calls account was linked to another DataHive account before,
          activity from before your link may not be counted here.
        </Trans>
      </li>
    </ul>
  </div>
)
